import { medicationRequest } from "../utils/medicationRequest";
import { useState, useRef, useCallback, useEffect } from "react";
import {
  Pill, Search, Camera, Upload, X, Loader2, Sparkles,
  FileText, Utensils, Clock, AlertCircle, AlertTriangle,
  Shield, RefreshCw, Lock, Plus, Bot, User, Send,
  ChevronDown, ChevronUp, Info,
} from "lucide-react";
import { computePerceptualHash } from "../utils/imageHash";
import { checkCreditsForAction, getCreditsForEndpoint } from "../hooks/useCredits";
import { supabase } from "../supabase";

type Lang = "ar" | "en";
type Dir = "rtl" | "ltr";

export interface MedicationInfo {
  name: string;
  activeIngredient: string;
  uses: string[];
  howToTake: {
    dosage: string;
    timing: string;
    foodRelation: string;
  };
  commonSideEffects: string[];
  seriousSideEffects: string[];
  warnings: string[];
  interactions: string[];
  missedDose: string;
  storage: string;
  disclaimer: string;
}

interface Props {
  lang: Lang;
  dir: Dir;
  refreshCredits: () => void | Promise<void>;
  onAddToReminder: (name: string) => void;
}

export function MedicationAssistant({ lang, dir, onAddToReminder, refreshCredits }: Props) {
  const actionBusy = useRef(false);
  const chatBusy = useRef(false);
  const isAr = lang === "ar";
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [medInfo, setMedInfo] = useState<MedicationInfo | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>("uses");
  const [showChat, setShowChat] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setError(null); }, [lang]);

  const doLookup = useCallback(async (medName: string) => {
    if (!medName.trim() || actionBusy.current) return;
    actionBusy.current = true;
    setLoading(true);
    setError(null);
    setMedInfo(null);
    setAdded(false);
    setExpandedSection("uses");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setError(isAr ? "سجّل الدخول لاستخدام ميزات الذكاء الاصطناعي" : "Sign in to use AI features"); actionBusy.current = false; setLoading(false); return; }
      const creditCheck = await checkCreditsForAction(getCreditsForEndpoint("medication-lookup"));
      if (!creditCheck.allowed) {
        setError(isAr ? "ليس لديك رصيد كافٍ. اشترك للحصول على المزيد." : "Not enough credits. Subscribe for more.");
        actionBusy.current = false; setLoading(false);
        return;
      }
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/medication-lookup`;
      const res = await medicationRequest(apiUrl, session?.access_token, { medicationName: medName, lang }, refreshCredits);
      if (res.ok) {
        const data = await res.json();
        setMedInfo(data as MedicationInfo);
      } else if (res.status === 402) {
        setError(isAr ? "ليس لديك رصيد كافٍ" : "Not enough credits");
      } else {
        let errDetail = "";
        try { const errData = await res.json(); errDetail = errData.error || ""; } catch { /* ignore */ }
        setError(errDetail || (isAr ? "تعذر الحصول على المعلومات" : "Could not retrieve information"));
      }
    } catch {
      setError(isAr ? "حدث خطأ في الاتصال. حاول مرة أخرى" : "Connection error. Try again");
    }
    actionBusy.current = false; setLoading(false);
  }, [lang, isAr, refreshCredits]);

  const handleSearch = () => {
    if (searchInput.trim()) doLookup(searchInput.trim());
  };

  const handleScan = async (file: File) => {
    if (actionBusy.current) return;
    actionBusy.current = true;
    setLoading(true);
    setError(null);
    setMedInfo(null);
    setAdded(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setError(isAr ? "سجّل الدخول لاستخدام ميزات الذكاء الاصطناعي" : "Sign in to use AI features"); actionBusy.current = false; setLoading(false); return; }
      const creditCheck = await checkCreditsForAction(getCreditsForEndpoint("medication-scan"));
      if (!creditCheck.allowed) {
        setError(isAr ? "ليس لديك رصيد كافٍ. اشترك للحصول على المزيد." : "Not enough credits. Subscribe for more.");
        actionBusy.current = false; setLoading(false);
        return;
      }
      const base64 = await fileToBase64(file);
      const dataUrl = `data:${file.type || "image/jpeg"};base64,${base64}`;
      const pHash = await computePerceptualHash(dataUrl);
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/medication-scan`;
      const res = await medicationRequest(apiUrl, session?.access_token, { image: base64, mimeType: file.type || "image/jpeg", lang, imageHash: pHash }, refreshCredits);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data && !data.error) {
        setMedInfo(data as MedicationInfo);
        setSearchInput(data.name || "");
      } else if (res.status === 402) {
        setError(isAr ? "ليس لديك رصيد كافٍ" : "Not enough credits");
      } else {
        setError(data.error || (isAr ? "تعذر التعرف على الدواء من الصورة" : "Could not identify medication from image"));
      }
    } catch {
      setError(isAr ? "حدث خطأ في معالجة الصورة" : "Error processing image");
    }
    actionBusy.current = false; setLoading(false);
  };

  const sendChatMessage = async () => {
    const q = chatInput.trim();
    if (!q || chatBusy.current) return;
    chatBusy.current = true;
    const userMsg = { role: "user" as const, content: q };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput("");
    setChatLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "سجّل الدخول لاستخدام ميزات الذكاء الاصطناعي" : "Sign in to use AI features" }]); chatBusy.current = false; setChatLoading(false); return; }
      const creditCheck = await checkCreditsForAction(getCreditsForEndpoint("medication-chat"));
      if (!creditCheck.allowed) {
        setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "ليس لديك رصيد كافٍ" : "Not enough credits" }]);
        chatBusy.current = false; setChatLoading(false); return;
      }
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/medication-chat`;
      const res = await medicationRequest(apiUrl, session?.access_token, {
          question: q,
          medications: medInfo ? [{ name: medInfo.name, dosage: medInfo.howToTake.dosage, frequency: medInfo.howToTake.timing, food_relation: medInfo.howToTake.foodRelation, times: [] }] : [],
          history: chatMessages,
          lang,
        }, refreshCredits);
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: "assistant", content: data.answer || (isAr ? "عذراً، لم أتمكن من الإجابة" : "Sorry, I couldn't answer") }]);
      } else {
        setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "عذراً، الخدمة غير متاحة حالياً" : "Sorry, the service is currently unavailable" }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: "assistant", content: isAr ? "عذراً، حدث خطأ. حاول مرة أخرى" : "Sorry, an error occurred. Try again" }]);
    }
    chatBusy.current = false; setChatLoading(false);
  };

  const toggleSection = (id: string) => {
    setExpandedSection(prev => prev === id ? null : id);
  };

  const handleAddToReminder = () => {
    if (medInfo) {
      onAddToReminder(medInfo.name);
      setAdded(true);
    }
  };

  const sections = medInfo ? [
    { id: "uses", icon: FileText, color: "blue", title: isAr ? "دواعي الاستعمال" : "Uses", content: medInfo.uses, type: "list" as const },
    { id: "howToTake", icon: Utensils, color: "cyan", title: isAr ? "طريقة التناول" : "How to Take", type: "howToTake" as const },
    { id: "commonSide", icon: AlertCircle, color: "amber", title: isAr ? "الآثار الجانبية الشائعة" : "Common Side Effects", content: medInfo.commonSideEffects, type: "list" as const },
    { id: "seriousSide", icon: AlertTriangle, color: "red", title: isAr ? "الآثار الجانبية الخطيرة" : "Serious Side Effects", content: medInfo.seriousSideEffects, type: "list" as const },
    { id: "warnings", icon: Shield, color: "orange", title: isAr ? "التحذيرات" : "Warnings", content: medInfo.warnings, type: "list" as const },
    { id: "interactions", icon: RefreshCw, color: "purple", title: isAr ? "التفاعلات" : "Interactions", content: medInfo.interactions, type: "list" as const },
    { id: "missedDose", icon: Clock, color: "indigo", title: isAr ? "الجرعة المنسية" : "Missed Dose", content: medInfo.missedDose, type: "text" as const },
    { id: "storage", icon: Lock, color: "teal", title: isAr ? "التخزين" : "Storage", content: medInfo.storage, type: "text" as const },
  ] : [];

  const colorMap: Record<string, { header: string; border: string; text: string; iconBg: string; dot: string; contentBg: string }> = {
    blue: { header: "bg-blue-600", border: "border-blue-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-blue-200", contentBg: "bg-white dark:bg-slate-800" },
    cyan: { header: "bg-cyan-600", border: "border-cyan-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-cyan-200", contentBg: "bg-white dark:bg-slate-800" },
    amber: { header: "bg-amber-600", border: "border-amber-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-amber-200", contentBg: "bg-white dark:bg-slate-800" },
    red: { header: "bg-red-600", border: "border-red-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-red-200", contentBg: "bg-white dark:bg-slate-800" },
    orange: { header: "bg-orange-600", border: "border-orange-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-orange-200", contentBg: "bg-white dark:bg-slate-800" },
    purple: { header: "bg-purple-600", border: "border-purple-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-purple-200", contentBg: "bg-white dark:bg-slate-800" },
    indigo: { header: "bg-indigo-600", border: "border-indigo-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-indigo-200", contentBg: "bg-white dark:bg-slate-800" },
    teal: { header: "bg-teal-600", border: "border-teal-600", text: "text-white", iconBg: "bg-white/20", dot: "bg-teal-200", contentBg: "bg-white dark:bg-slate-800" },
  };

  return (
    <div className="space-y-4" dir={dir}>
      {/* Search & Scan Card */}
      <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-blue-600 p-5 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black text-white">
              {isAr ? "مساعد الأدوية الذكي" : "AI Medication Assistant"}
            </h2>
            <p className="text-sm text-white/80">
              {isAr ? "ابحث أو امسح العلبة لعرض معلومات عامة عن الدواء" : "Search or scan a package for general medication information"}
            </p>
          </div>
        </div>
        <p className="text-sm text-white mb-3">{isAr ? "البحث: رصيد واحد • الصورة: رصيدان • سؤال المحادثة: رصيد واحد. المعلومات ليست وصفة طبية." : "Search: 1 credit • Image: 2 credits • Chat question: 1 credit. Information is not a prescription."}</p>
        {/* Scan & Upload Buttons - prominent, above search */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className="py-3.5 rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-sm sm:text-base disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          >
            <Camera className="w-5 h-5 sm:w-6 sm:h-6" />
            {isAr ? "مسح بالكاميرا" : "Scan with Camera"}
          </button>
          <button
            onClick={() => galleryInputRef.current?.click()}
            disabled={loading}
            className="py-3.5 rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-sm sm:text-base disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          >
            <Upload className="w-5 h-5 sm:w-6 sm:h-6" />
            {isAr ? "رفع صورة" : "Upload Image"}
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleScan(f); e.currentTarget.value = ""; }}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) handleScan(f); e.currentTarget.value = ""; }}
        />
        {/* Search Row */}
        <div className="flex gap-2">
          <input
            aria-label={isAr ? "اسم الدواء للبحث" : "Medication search"}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleSearch(); }}
            placeholder={isAr ? "اكتب اسم الدواء..." : "Type medication name..."}
            className="flex-1 min-w-0 bg-white/95 dark:bg-slate-800 border-2 border-white/30 rounded-xl px-4 py-3.5 text-base text-stone-800 dark:text-slate-100 outline-none focus:border-white"
          />
          <button
            aria-label={isAr ? "بحث — رصيد واحد" : "Search — 1 credit"}
            onClick={handleSearch}
            disabled={loading || !searchInput.trim()}
            className="w-12 h-12 rounded-xl bg-white text-violet-600 disabled:opacity-50 flex items-center justify-center transition-all active:scale-95 shrink-0"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="rounded-2xl bg-gradient-to-br from-violet-50 via-blue-50 to-cyan-50 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900 border-2 border-violet-200 dark:border-violet-500/30 p-8 overflow-hidden relative">
          {/* Floating medication icons */}
          <div className="relative h-40 flex items-center justify-center">
            {/* Capsule 1 */}
            <div className="absolute animate-[medFloat_3s_ease-in-out_infinite]" style={{ animationDelay: "0ms" }}>
              <div className="w-14 h-7 rounded-full bg-gradient-to-r from-violet-500 to-blue-500 shadow-lg flex items-center justify-center" style={{ transform: "rotate(-20deg)" }}>
                <div className="w-1/2 h-full rounded-l-full bg-violet-400/60" />
              </div>
            </div>
            {/* Pill 2 */}
            <div className="absolute animate-[medFloat_2.5s_ease-in-out_infinite]" style={{ animationDelay: "300ms", transform: "translate(40px, -30px)" }}>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 shadow-lg" />
            </div>
            {/* Capsule 3 */}
            <div className="absolute animate-[medFloat_3.2s_ease-in-out_infinite]" style={{ animationDelay: "600ms", transform: "translate(-50px, 25px)" }}>
              <div className="w-12 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 shadow-lg" style={{ transform: "rotate(15deg)" }}>
                <div className="w-1/2 h-full rounded-l-full bg-emerald-300/60" />
              </div>
            </div>
            {/* Pill 4 */}
            <div className="absolute animate-[medFloat_2.8s_ease-in-out_infinite]" style={{ animationDelay: "900ms", transform: "translate(55px, 35px)" }}>
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg" />
            </div>
            {/* Pill 5 */}
            <div className="absolute animate-[medFloat_3.5s_ease-in-out_infinite]" style={{ animationDelay: "1200ms", transform: "translate(-40px, -40px)" }}>
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 shadow-lg" />
            </div>
            {/* Center pulse ring */}
            <div className="absolute w-20 h-20 rounded-full bg-violet-500/10 animate-ping" style={{ animationDuration: "2s" }} />
            <div className="absolute w-16 h-16 rounded-full bg-violet-500/20 animate-pulse flex items-center justify-center">
              <Pill className="w-8 h-8 text-violet-600 dark:text-violet-300" />
            </div>
          </div>
          <p className="text-base font-bold text-violet-700 dark:text-violet-300 text-center mt-2">
            {isAr ? "جاري تحليل الدواء..." : "Analyzing medication..."}
          </p>
          <div className="mt-3 mx-auto w-48 h-1.5 rounded-full bg-violet-200 dark:bg-violet-500/20 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-500 animate-[medProgress_2s_ease-in-out_infinite]" />
          </div>
          <p className="text-sm text-stone-500 dark:text-slate-400 text-center mt-3">
            {isAr ? "قد يستغرق هذا بضع ثوانٍ" : "This may take a few seconds"}
          </p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="rounded-2xl bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 p-5 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 shrink-0" />
          <p className="text-base font-medium text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Medication Info Cards */}
      {medInfo && !loading && (
        <>
          {/* Name & Active Ingredient Header */}
          <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 p-5 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
                <Pill className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-black text-white">{medInfo.name}</h3>
                <p className="text-base text-white/90 mt-1">
                  {isAr ? "المادة الفعالة: " : "Active ingredient: "}
                  <span className="font-bold">{medInfo.activeIngredient}</span>
                </p>
              </div>
              <button
                onClick={() => setShowChat(true)}
                className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/20 hover:bg-white/30 text-white font-bold text-sm transition-all active:scale-95"
              >
                <Bot className="w-5 h-5" />
                <span className="hidden sm:inline">{isAr ? "اسأل الذكاء" : "Ask AI"}</span>
              </button>
            </div>
          </div>

          {/* Section Cards */}
          {sections.map(section => {
            const c = colorMap[section.color];
            const isOpen = expandedSection === section.id;
            const Icon = section.icon;
            return (
              <div key={section.id} className={`rounded-2xl ${c.contentBg} border-2 ${c.border} overflow-hidden shadow-sm transition-all`}>
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center gap-3 p-4 ${c.header} text-left`}
                >
                  <div className={`w-10 h-10 rounded-xl ${c.iconBg} flex items-center justify-center shrink-0`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="flex-1 text-lg font-bold text-white">{section.title}</h4>
                  {isOpen
                    ? <ChevronUp className="w-5 h-5 text-white shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-white shrink-0" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-3">
                    {section.type === "list" && Array.isArray(section.content) && (
                      <ul className="space-y-2.5">
                        {(section.content as string[]).map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className={`mt-1.5 w-2.5 h-2.5 rounded-full ${c.dot} shrink-0`} />
                            <span className="text-base font-medium text-stone-800 dark:text-slate-100 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.type === "howToTake" && medInfo.howToTake && (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <span className={`mt-1.5 w-2.5 h-2.5 rounded-full ${c.dot} shrink-0`} />
                          <div>
                            <span className="text-sm font-bold text-stone-500 dark:text-slate-400">{isAr ? "الجرعة: " : "Dosage: "}</span>
                            <span className="text-base font-medium text-stone-800 dark:text-slate-100">{medInfo.howToTake.dosage}</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className={`mt-1.5 w-2.5 h-2.5 rounded-full ${c.dot} shrink-0`} />
                          <div>
                            <span className="text-sm font-bold text-stone-500 dark:text-slate-400">{isAr ? "المواعيد: " : "Timing: "}</span>
                            <span className="text-base font-medium text-stone-800 dark:text-slate-100">{medInfo.howToTake.timing}</span>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className={`mt-1.5 w-2.5 h-2.5 rounded-full ${c.dot} shrink-0`} />
                          <div>
                            <span className="text-sm font-bold text-stone-500 dark:text-slate-400">{isAr ? "علاقة بالطعام: " : "Food: "}</span>
                            <span className="text-base font-medium text-stone-800 dark:text-slate-100">{medInfo.howToTake.foodRelation}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {section.type === "text" && (
                      <p className="text-base font-medium text-stone-800 dark:text-slate-100 leading-relaxed pl-5">{section.content as string}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add to Reminder Button */}
          <button
            onClick={handleAddToReminder}
            disabled={added}
            className={`w-full py-4 rounded-2xl font-bold text-lg shadow-lg transition-all flex items-center justify-center gap-2 active:scale-[0.98] ${
              added
                ? "bg-emerald-500 text-white"
                : "bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
            }`}
          >
            {added ? (
              <>
                <Sparkles className="w-6 h-6" />
                {isAr ? "تمت الإضافة إلى التذكير" : "Added to Reminder"}
              </>
            ) : (
              <>
                <Plus className="w-6 h-6" />
                {isAr ? "إضافة إلى تذكير الأدوية" : "Add to Medication Reminder"}
              </>
            )}
          </button>

          {/* Disclaimer */}
          <div className="rounded-xl bg-stone-100 dark:bg-slate-700/50 border border-stone-200 dark:border-slate-600 p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-stone-400 dark:text-slate-400 shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-stone-600 dark:text-slate-300 leading-relaxed">
              {medInfo.disclaimer || (isAr
                ? "هذه معلومات عامة وليست بديلاً عن استشارة الطبيب أو الصيدلي"
                : "This is general information and not a substitute for consulting a doctor or pharmacist")}
            </p>
          </div>
        </>
      )}

      {/* Chat Panel */}
      {showChat && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowChat(false)}>
          <div
            className="w-full sm:max-w-md bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[80vh]"
            onClick={e => e.stopPropagation()}
            dir={dir}
          >
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 border-b border-stone-200 dark:border-slate-700 bg-gradient-to-r from-violet-600 to-blue-600 rounded-t-3xl">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">
                  {isAr ? "اسأل عن الدواء" : "Ask About medication"}
                </h3>
                <p className="text-xs text-white/70">
                  {medInfo ? medInfo.name : (isAr ? "اسألني أي سؤال" : "Ask me anything")}
                </p>
              </div>
              <button onClick={() => setShowChat(false)} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Chat Messages */}
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[50vh]">
              {chatMessages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bot className="w-12 h-12 text-violet-400 mb-2" />
                  <p className="text-stone-500 dark:text-slate-400 font-medium text-sm">
                    {isAr ? "مرحباً! اسألني أي سؤال عن هذا الدواء" : "Hi! Ask me any question about this medication"}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 justify-center">
                    {[
                      isAr ? "هل هو آمن للحامل؟" : "Is it safe during pregnancy?",
                      isAr ? "ما الآثار الجانبية؟" : "What are the side effects?",
                      isAr ? "هل يسبب النعاس؟" : "Does it cause drowsiness?",
                    ].map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setChatInput(s)}
                        className="px-3 py-1.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-300 text-xs font-bold border border-violet-200 dark:border-violet-500/20 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((msg, i) => {
                const isUser = msg.role === "user";
                const isError = !isUser && msg.content.startsWith(isAr ? "عذراً" : "Sorry");
                const isWarn = !isUser && (msg.content.includes(isAr ? "تحذير" : "warning") || msg.content.includes(isAr ? "تفاعل" : "interaction"));
                const lines = msg.content.split("\n").filter(l => l.trim());
                const isList = lines.length > 1;
                return (
                  <div key={i} className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}>
                    <div className={`w-10 h-10 rounded-full shrink-0 flex items-center justify-center shadow-sm ${
                      isUser ? "bg-blue-100 dark:bg-blue-500/20" : isError ? "bg-red-100 dark:bg-red-500/20" : isWarn ? "bg-amber-100 dark:bg-amber-500/20" : "bg-violet-100 dark:bg-violet-500/20"
                    }`}>
                      {isUser
                        ? <User className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                        : isError
                          ? <AlertCircle className="w-5 h-5 text-red-500" />
                          : isWarn
                            ? <AlertTriangle className="w-5 h-5 text-amber-500" />
                            : <Bot className="w-5 h-5 text-violet-600 dark:text-violet-300" />}
                    </div>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 leading-relaxed ${
                      isUser
                        ? "bg-blue-600 text-white rounded-tr-sm"
                        : isError
                          ? "bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/30 text-red-800 dark:text-red-200 rounded-tl-sm"
                          : isWarn
                            ? "bg-amber-50 dark:bg-amber-500/10 border-2 border-amber-200 dark:border-amber-500/30 text-amber-900 dark:text-amber-100 rounded-tl-sm"
                            : "bg-violet-50 dark:bg-violet-500/10 border-2 border-violet-200 dark:border-violet-500/30 text-stone-800 dark:text-slate-100 rounded-tl-sm"
                    }`}>
                      {isList && !isUser ? (
                        <ul className="space-y-2">
                          {lines.map((line, idx) => {
                            const cleanLine = line.replace(/^[-•*\d+.\)]+\s*/, "").trim();
                            if (!cleanLine) return null;
                            return (
                              <li key={idx} className="flex items-start gap-2.5">
                                <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${
                                  isError ? "bg-red-400" : isWarn ? "bg-amber-400" : "bg-violet-400"
                                }`} />
                                <span className="text-base font-medium">{cleanLine}</span>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p className="text-base font-medium whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                );
              })}
              {chatLoading && (
                <div className="flex gap-2.5">
                  <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center bg-violet-100 dark:bg-violet-500/20">
                    <Bot className="w-5 h-5 text-violet-600 dark:text-violet-300" />
                  </div>
                  <div className="bg-violet-50 dark:bg-violet-500/10 border-2 border-violet-200 dark:border-violet-500/30 rounded-2xl rounded-tl-sm px-5 py-4 flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t border-stone-200 dark:border-slate-700 flex gap-2">
              <input
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") sendChatMessage(); }}
                placeholder={isAr ? "اكتب سؤالك هنا..." : "Type your question here..."}
                className="flex-1 bg-stone-50 dark:bg-slate-700 border-2 border-stone-200 dark:border-slate-600 rounded-xl px-4 py-3.5 text-stone-800 dark:text-slate-100 outline-none focus:border-violet-500 text-base"
              />
              <button
                onClick={sendChatMessage}
                disabled={!chatInput.trim() || chatLoading}
                className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 disabled:opacity-50 text-white flex items-center justify-center transition-all active:scale-95 shrink-0"
              >
                {chatLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
